import React, { useState } from 'react';
import ExpressionInput from './ExpressionInput';

const OPERATORS = [
  { value: '==',          label: 'equals' },
  { value: '!=',          label: 'does not equal' },
  { value: '>',           label: 'is greater than' },
  { value: '>=',          label: 'is greater than or equal to' },
  { value: '<',           label: 'is less than' },
  { value: '<=',          label: 'is less than or equal to' },
  { value: 'contains',    label: 'contains' },
  { value: 'not_contains',label: 'does not contain' },
  { value: 'in',          label: 'is in' },
  { value: 'not_in',      label: 'is not in' },
  { value: 'exists',      label: 'is set' },
  { value: 'not_exists',  label: 'is not set' },
  { value: 'is_true',     label: 'is true' },
  { value: 'is_false',    label: 'is false' },
];

const NO_RIGHT = new Set(['exists', 'not_exists', 'is_true', 'is_false']);

const OPERATOR_LABELS = Object.fromEntries(OPERATORS.map(o => [o.value, o.label]));

function buildPreview(conditions, logic) {
  if (!conditions || !conditions.length) return 'No conditions yet.';
  const connector = logic === 'any' ? ' OR ' : ' AND ';
  return conditions.map(c => {
    const op = OPERATOR_LABELS[c.operator] || c.operator || '?';
    const left = c.left ? `"${c.left}"` : '…';
    if (NO_RIGHT.has(c.operator)) return `${left} ${op}`;
    const right = (c.right !== undefined && c.right !== null && c.right !== '') ? `"${c.right}"` : '…';
    return `${left} ${op} ${right}`;
  }).join(connector);
}

const inputSt = {
  padding: '6px 8px',
  border: '1px solid var(--border-color)',
  borderRadius: '4px',
  background: 'var(--bg-input)',
  color: 'var(--text-primary)',
  fontSize: '12px',
  boxSizing: 'border-box',
  width: '100%',
};

const btnSt = (active) => ({
  padding: '3px 10px',
  border: '1px solid var(--border-color)',
  borderRadius: '4px',
  background: active ? 'var(--accent-color)' : 'var(--bg-tertiary)',
  color: active ? '#fff' : 'var(--text-primary)',
  fontSize: '11px',
  cursor: 'pointer',
  fontWeight: active ? 600 : 400,
});

export default function ConditionBuilder({
  conditions,   // Array<{left, operator, right}>
  logic,        // 'all' | 'any'
  onChange,     // (newConditions, newLogic) => void
  ancestorNodes,
  nodeStatus,
  envVars,
}) {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [advancedJson, setAdvancedJson] = useState('');
  const [advancedError, setAdvancedError] = useState('');

  const safeConditions = Array.isArray(conditions) ? conditions : [];
  const safeLogic = logic === 'any' ? 'any' : 'all';

  const updateCondition = (idx, key, val) => {
    const updated = safeConditions.map((c, i) => i === idx ? { ...c, [key]: val } : c);
    onChange(updated, safeLogic);
  };

  const addCondition = () => {
    onChange([...safeConditions, { left: '', operator: '==', right: '' }], safeLogic);
  };

  const removeCondition = (idx) => {
    onChange(safeConditions.filter((_, i) => i !== idx), safeLogic);
  };

  const setLogic = (val) => onChange(safeConditions, val);

  const openAdvanced = () => {
    setAdvancedJson(JSON.stringify(safeConditions, null, 2));
    setAdvancedError('');
    setShowAdvanced(true);
  };

  const applyAdvanced = () => {
    try {
      const parsed = JSON.parse(advancedJson);
      if (!Array.isArray(parsed)) throw new Error('Must be an array');
      onChange(parsed, safeLogic);
      setShowAdvanced(false);
      setAdvancedError('');
    } catch (e) {
      setAdvancedError(e.message);
    }
  };

  return (
    <div style={{ marginBottom: '15px' }}>
      {/* Logic toggle */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
        <span style={{ fontSize: '11px', color: 'var(--text-secondary)', fontWeight: 600 }}>Match</span>
        <button
          style={btnSt(safeLogic === 'all')}
          onClick={() => setLogic('all')}
          title="All conditions must be true (AND)"
        >ALL (AND)</button>
        <button
          style={btnSt(safeLogic === 'any')}
          onClick={() => setLogic('any')}
          title="Any condition can be true (OR)"
        >ANY (OR)</button>
        <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>conditions</span>
      </div>

      {/* Condition rows */}
      {safeConditions.map((cond, idx) => (
        <div key={idx} style={{ marginBottom: '8px', background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)', borderRadius: '6px', padding: '8px' }}>
          {/* Top: Value label + remove button */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '3px' }}>
            <span style={{ fontSize: '10px', color: 'var(--text-tertiary)' }}>Value</span>
            <button
              onClick={() => removeCondition(idx)}
              title="Remove condition"
              style={{ background: 'none', border: 'none', color: 'var(--text-tertiary)', cursor: 'pointer', fontSize: '14px', padding: '0 2px', lineHeight: 1 }}
            >×</button>
          </div>

          {/* Value input (full width) */}
          <ExpressionInput
            value={cond.left || ''}
            onChange={val => updateCondition(idx, 'left', val)}
            placeholder="{{ nodes.NodeName.output }}"
            ancestorNodes={ancestorNodes}
            nodeStatus={nodeStatus}
            envVars={envVars}
            style={{ marginBottom: 0 }}
          />

          {/* Operator + Compare to on one row */}
          <div style={{ display: 'flex', gap: '6px', alignItems: 'flex-end', marginTop: '6px' }}>
            <div style={{ flex: '0 0 auto', minWidth: 0 }}>
              <div style={{ fontSize: '10px', color: 'var(--text-tertiary)', marginBottom: '3px' }}>Operator</div>
              <select
                value={cond.operator || '=='}
                onChange={e => updateCondition(idx, 'operator', e.target.value)}
                style={{ ...inputSt, marginBottom: 0, width: 'auto', maxWidth: '130px' }}
              >
                {OPERATORS.map(op => (
                  <option key={op.value} value={op.value}>{op.label}</option>
                ))}
              </select>
            </div>

            {!NO_RIGHT.has(cond.operator) && (
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '10px', color: 'var(--text-tertiary)', marginBottom: '3px' }}>Compare to</div>
                <ExpressionInput
                  value={cond.right !== undefined && cond.right !== null ? String(cond.right) : ''}
                  onChange={val => updateCondition(idx, 'right', val)}
                  placeholder="value or {{ expression }}"
                  ancestorNodes={ancestorNodes}
                  nodeStatus={nodeStatus}
                  envVars={envVars}
                  style={{ marginBottom: 0 }}
                />
              </div>
            )}
          </div>
        </div>
      ))}

      {/* Add / Advanced */}
      <div style={{ display: 'flex', gap: '6px', marginTop: '4px' }}>
        <button onClick={addCondition} style={{ ...btnSt(false), fontSize: '12px', padding: '4px 10px' }}>
          + Add Condition
        </button>
        <button onClick={showAdvanced ? () => setShowAdvanced(false) : openAdvanced} style={{ ...btnSt(showAdvanced), fontSize: '11px', padding: '4px 10px' }}>
          {showAdvanced ? 'Hide Advanced' : 'Advanced (JSON)'}
        </button>
      </div>

      {/* Plain-English preview */}
      {safeConditions.length > 0 && !showAdvanced && (
        <div style={{ marginTop: '10px', padding: '8px 10px', background: 'var(--bg-tertiary)', borderRadius: '6px', fontSize: '11px', color: 'var(--text-secondary)', borderLeft: '3px solid var(--accent-color)', wordBreak: 'break-word' }}>
          <span style={{ fontWeight: 600, color: 'var(--text-tertiary)' }}>Preview: </span>
          {buildPreview(safeConditions, safeLogic)}
        </div>
      )}

      {/* Advanced JSON editor */}
      {showAdvanced && (
        <div style={{ marginTop: '10px' }}>
          <textarea
            value={advancedJson}
            onChange={e => setAdvancedJson(e.target.value)}
            style={{ width: '100%', minHeight: '120px', padding: '8px', background: 'var(--bg-input)', color: 'var(--text-primary)', border: `1px solid ${advancedError ? 'var(--error-color)' : 'var(--border-color)'}`, borderRadius: '4px', fontSize: '11px', fontFamily: 'monospace', resize: 'vertical', boxSizing: 'border-box' }}
            spellCheck={false}
          />
          {advancedError && (
            <div style={{ fontSize: '11px', color: 'var(--error-color)', marginTop: '3px' }}>{advancedError}</div>
          )}
          <button onClick={applyAdvanced} style={{ ...btnSt(true), marginTop: '6px', fontSize: '12px', padding: '4px 12px' }}>
            Apply
          </button>
        </div>
      )}
    </div>
  );
}
